/**
 * AdminCalendarRulesCard
 *
 * One unified card on the admin dashboard for the 4 calendar rules:
 *   1. Muhurtham coverage status (read-only — must be >= 13 months ahead)
 *   2. Monthly leads cap per role bucket (vendor / venue / pro / ent.pro)
 *   3. Daily leads cap per role bucket
 *   4. Lift fee (₹) for vendors who want to manually unblock a system block
 *
 * Saves to backend via `PUT /api/admin/calendar-leads-rules`.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Warning, CheckCircle, FloppyDisk, Spinner } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKETS = [
  { key: 'vendor', label: 'Vendor', hint: 'Caterer, photographer, decorator, etc.' },
  { key: 'venue', label: 'Venue', hint: 'Mandapam, banquet hall, villa' },
  { key: 'professional', label: 'Professional', hint: 'Chef, makeup, priest, mehndi…' },
  { key: 'entertainment_pro', label: 'Entertainment Pro', hint: 'DJ, magician, dancer, anchor…' },
];

const AdminCalendarRulesCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [monthly, setMonthly] = useState({ vendor: 30, venue: 20, professional: 30, entertainment_pro: 20 });
  const [daily, setDaily] = useState({ vendor: 5, venue: 3, professional: 5, entertainment_pro: 3 });
  const [liftFee, setLiftFee] = useState(100);
  const [muhurtham, setMuhurtham] = useState(null);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/calendar-leads-rules`,
        { withCredentials: true });
      setMonthly(data.monthly_caps);
      setDaily(data.daily_caps);
      setLiftFee(data.lift_fee_inr);
      setMuhurtham(data.muhurtham);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load calendar rules');
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BACKEND_URL}/api/admin/calendar-leads-rules`,
        { monthly_caps: monthly, daily_caps: daily, lift_fee_inr: Number(liftFee) },
        { withCredentials: true });
      setMonthly(data.monthly_caps);
      setDaily(data.daily_caps);
      setLiftFee(data.lift_fee_inr);
      setMuhurtham(data.muhurtham);
      toast.success('Calendar rules saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const updateBucket = (setter) => (key, value) => {
    const n = parseInt(value, 10);
    setter(prev => ({ ...prev, [key]: Number.isFinite(n) ? Math.max(1, Math.min(10000, n)) : 1 }));
  };

  return (
    <Card className="rounded-lg border-[#E8E1D5]" data-testid="admin-calendar-rules-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar size={20} weight="fill" className="text-brand-primary" />
          Calendar Rules
        </CardTitle>
        <CardDescription>
          Monthly &amp; daily lead caps per role · Muhurtham coverage · Lift fee.
          A &ldquo;lead&rdquo; = customer received phone, OR vendor declined, OR vendor didn&apos;t respond.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <p className="text-sm text-text-secondary flex items-center gap-2">
            <Spinner className="animate-spin" size={14} /> Loading rules…
          </p>
        ) : (
          <>
            {/* RULE 1 — Muhurtham coverage */}
            <div className="border border-[#E8E1D5] rounded p-3 space-y-2"
              data-testid="rule-1-muhurtham-block">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-brand-accent/15 text-brand-primary px-2 py-0.5">Rule 1</span>
                <span className="text-sm font-bold">Muhurtham coverage</span>
              </div>
              {muhurtham && (
                <div className="flex items-center gap-2 text-xs">
                  {muhurtham.ok ? (
                    <><CheckCircle size={14} weight="fill" className="text-green-600" />
                      <span className="text-green-800">
                        Covered <strong>{muhurtham.months_ahead}</strong> months ahead (until <strong>{muhurtham.covered_until}</strong>).
                        {muhurtham.future_count} future muhurtham dates marked.
                      </span></>
                  ) : (
                    <><Warning size={14} weight="fill" className="text-red-600" />
                      <span className="text-red-800">
                        Only <strong>{muhurtham.months_ahead}</strong> months ahead — must be &ge; 13.
                        Add more dates in <code className="px-1 bg-stone-100">/app/backend/muhurtham_dates.py</code>.
                      </span></>
                  )}
                </div>
              )}
            </div>

            {/* RULE 2 — Monthly leads cap */}
            <div className="border border-[#E8E1D5] rounded p-3 space-y-3"
              data-testid="rule-2-monthly-cap-block">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-brand-accent/15 text-brand-primary px-2 py-0.5">Rule 2</span>
                <span className="text-sm font-bold">Monthly leads cap per role</span>
              </div>
              <p className="text-[11px] text-text-secondary -mt-1">
                When a user hits this many leads in a calendar month, the rest of the month is auto-blocked.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BUCKETS.map(b => (
                  <div key={b.key} className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-wider">{b.label}</Label>
                    <Input type="number" min="1" max="10000"
                      value={monthly[b.key] ?? ''}
                      onChange={(e) => updateBucket(setMonthly)(b.key, e.target.value)}
                      className="rounded-none border-[#E8E1D5] h-9 font-mono"
                      data-testid={`rule-2-monthly-${b.key}`} />
                    <p className="text-[10px] text-text-secondary truncate">{b.hint}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* RULE 3 — Daily leads cap */}
            <div className="border border-[#E8E1D5] rounded p-3 space-y-3"
              data-testid="rule-3-daily-cap-block">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-brand-accent/15 text-brand-primary px-2 py-0.5">Rule 3</span>
                <span className="text-sm font-bold">Daily leads cap per role</span>
              </div>
              <p className="text-[11px] text-text-secondary -mt-1">
                When a user hits this many leads in one day, that single day is auto-blocked.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {BUCKETS.map(b => (
                  <div key={b.key} className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-wider">{b.label}</Label>
                    <Input type="number" min="1" max="1000"
                      value={daily[b.key] ?? ''}
                      onChange={(e) => updateBucket(setDaily)(b.key, e.target.value)}
                      className="rounded-none border-[#E8E1D5] h-9 font-mono"
                      data-testid={`rule-3-daily-${b.key}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* RULE 4 — Lift fee */}
            <div className="border border-[#E8E1D5] rounded p-3 space-y-2"
              data-testid="rule-4-lift-fee-block">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-brand-accent/15 text-brand-primary px-2 py-0.5">Rule 4</span>
                <span className="text-sm font-bold">Lift fee (₹) per blocked day</span>
              </div>
              <p className="text-[11px] text-text-secondary">
                Vendors who hit their cap can manually unblock a day by paying this fee via Razorpay.
              </p>
              <div className="flex items-center gap-2 max-w-[200px]">
                <span className="text-sm font-bold text-text-secondary">₹</span>
                <Input type="number" min="0" max="100000"
                  value={liftFee ?? ''}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    setLiftFee(Number.isFinite(n) ? Math.max(0, Math.min(100000, n)) : 0);
                  }}
                  className="rounded-none border-[#E8E1D5] h-9 font-mono"
                  data-testid="rule-4-lift-fee-input" />
              </div>
            </div>

            <Button onClick={save} disabled={saving}
              className="bg-brand-primary text-sand-50 rounded-none h-10 px-5 disabled:opacity-50"
              data-testid="calendar-rules-save-btn">
              {saving
                ? <><Spinner size={14} className="mr-2 animate-spin" /> Saving…</>
                : <><FloppyDisk size={14} className="mr-2" weight="fill" /> Save calendar rules</>}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCalendarRulesCard;
